/**
 * Confirm Page
 * Booking summary and confirmation
 */

import {
  t,
  formatDate,
  formatCurrency,
  translateRoomType,
  translateRatePlan,
  getCurrentLanguage,
} from '../i18n';
import { Header, Card, SummaryRow, FloatingNav, BackToTopButton, initBackToTop } from '../components';
import { getState, setBooking, navigateTo, setLoading, setError, calculateNights, saveGuestInfoToStorage } from '../store';
import { createBooking, saveUserProfile } from '../api';
import { config } from '../config';
import type { BookingRequest } from '../types';
import { resetSentConfirmation } from './success';

/**
 * Render confirm page
 */
export function renderConfirmPage(): string {
  const state = getState();
  const { search, selectedRoom, guestInfo, bookingType } = state;

  if (!selectedRoom || !guestInfo) {
    navigateTo('search');
    return '';
  }

  const isSpa = bookingType === 'SPA';
  const isRest = bookingType === 'RESTAURANT';
  const isMice = bookingType === 'MICE';
  const isAppointment = bookingType === 'APPOINTMENT' || state.engine === 'appointment';
  const isRfp = bookingType === 'RFP' || state.engine === 'request_rfp';

  const lang = getCurrentLanguage();
  const nights = search ? calculateNights(search.checkIn, search.checkOut) : 1;
  const totalRateWithExtras = selectedRoom.totalRate + (state.extraCharges?.totalExtraCharge || 0);

  let detailsCardHtml = '';

  if (isAppointment && state.spaDetails) {
    const appt = state.spaDetails;
    const outletName = typeof appt.outletName === 'object'
      ? (appt.outletName[lang] || appt.outletName.en || appt.outletName.th || Object.values(appt.outletName)[0] || 'Appointment')
      : (appt.outletName || 'Appointment');
    const treatName = typeof appt.treatmentName === 'object'
      ? (appt.treatmentName[lang] || appt.treatmentName.en || appt.treatmentName.th || Object.values(appt.treatmentName)[0] || selectedRoom.roomTypeName)
      : (appt.treatmentName || selectedRoom.roomTypeName);

    const isSeminar = outletName.includes('CPD') || outletName.includes('สัมมนา') || outletName.includes('อบรม') || treatName.includes('CPD') || treatName.includes('สัมมนา') || treatName.includes('อบรม');

    detailsCardHtml = `
      <div class="summary-section">
        <h4>${isSeminar ? '🎓 ' + (lang === 'th' ? 'รายละเอียดการอบรมสัมมนา' : lang === 'zh' ? '研讨会/培训详情' : 'Seminar & Course Details') : '🩺 ' + (lang === 'th' ? 'รายละเอียดการนัดหมาย' : 'Appointment Details')}</h4>
        ${SummaryRow(isSeminar ? t('seminarCourse') : (lang === 'th' ? 'บริการ' : 'Service'), treatName)}
        ${outletName && outletName !== 'Appointment' ? SummaryRow(lang === 'th' ? 'สถานที่ / องค์กร' : 'Location / Provider', outletName) : ''}
        ${SummaryRow(t('serviceDate'), formatDate(appt.bookingDate))}
        ${SummaryRow(t('timeSlot'), appt.timeSlot ? `${appt.timeSlot}${lang === 'th' ? ' น.' : ''}` : '-')}
        ${SummaryRow(isSeminar ? (lang === 'th' ? 'จำนวนที่นั่งที่ลงทะเบียน' : 'Registered Seats') : (lang === 'th' ? 'จำนวนผู้รับบริการ' : 'Attendees'), `${appt.pax || 1} ${t('personUnit')}`)}
      </div>
    `;
  } else if (isRfp && state.miceDetails) {
    const rfp = state.miceDetails;
    detailsCardHtml = `
      <div class="summary-section">
        <h4>📋 ${lang === 'th' ? 'รายละเอียดคำขอใบเสนอราคา (RFP)' : 'Quotation Request Details'}</h4>
        ${SummaryRow(lang === 'th' ? 'สถานที่ / บริการ' : 'Location / Service', rfp.functionRoomName || selectedRoom.roomTypeName)}
        ${SummaryRow(lang === 'th' ? 'วันที่ต้องการ' : 'Requested Date', formatDate(rfp.eventDate))}
        ${SummaryRow(lang === 'th' ? 'จำนวนผู้เข้าร่วม / ขนาด' : 'Quantity / Scale', `${rfp.expectedPax}`)}
        ${rfp.specialRequests ? SummaryRow(lang === 'th' ? 'ความต้องการ' : 'Requirements', rfp.specialRequests) : ''}
      </div>
    `;
  } else if (isMice && state.miceDetails) {
    const mice = state.miceDetails;
    const roomTitle = typeof mice.functionRoomName === 'object'
      ? (mice.functionRoomName[lang] || mice.functionRoomName.en || mice.functionRoomName.th || Object.values(mice.functionRoomName)[0])
      : (mice.functionRoomName || selectedRoom.roomTypeName || 'Function Room');

    const layoutDisplay = typeof mice.layoutStyle === 'string'
      ? (mice.layoutStyle.charAt(0).toUpperCase() + mice.layoutStyle.slice(1))
      : 'Theater';

    detailsCardHtml = `
      <div class="summary-section">
        <h4>🏛️ ${lang === 'th' ? 'รายละเอียดการจัดงานประชุม & เลี้ยงสังสรรค์' : lang === 'zh' ? '会议与宴会详情' : lang === 'ru' ? 'Детали мероприятия и зала' : lang === 'ja' ? '会議・宴会詳細' : lang === 'ko' ? '행사 및 연회장 상세 정보' : 'Event & Function Room Details'}</h4>
        ${SummaryRow(lang === 'th' ? 'ห้องประชุม / สถานที่' : lang === 'zh' ? '场地' : lang === 'ru' ? 'Конференц-зал / Площадка' : lang === 'ja' ? '会場・会議室' : lang === 'ko' ? '회의실 / 장소' : 'Function Room', roomTitle)}
        ${SummaryRow(lang === 'th' ? 'วันที่จัดงาน' : lang === 'zh' ? '活动日期' : lang === 'ru' ? 'Дата мероприятия' : lang === 'ja' ? '開催日' : lang === 'ko' ? '행사 일자' : 'Event Date', formatDate(mice.eventDate))}
        ${SummaryRow(lang === 'th' ? 'ช่วงเวลา / รอบ' : lang === 'zh' ? '时段' : lang === 'ru' ? 'Время / Сессия' : lang === 'ja' ? '時間帯 / セッション' : lang === 'ko' ? '시간대 / 세션' : 'Session', mice.timeSlotName || 'Full-Day')}
        ${SummaryRow(lang === 'th' ? 'จำนวนผู้เข้าร่วม' : lang === 'zh' ? '参会人数' : lang === 'ru' ? 'Количество участников' : lang === 'ja' ? '参加人数' : lang === 'ko' ? '참석 인원' : 'Attendees', `${mice.expectedPax} ${t('personUnit')}`)}
        ${SummaryRow(lang === 'th' ? 'รูปแบบการจัดโต๊ะ' : lang === 'zh' ? '摆台形式' : lang === 'ru' ? 'Схема рассадки' : lang === 'ja' ? 'レイアウト' : lang === 'ko' ? '좌석 배치' : 'Layout', layoutDisplay)}
        ${mice.packageName ? SummaryRow(lang === 'th' ? 'แพ็กเกจ' : lang === 'zh' ? '套餐' : lang === 'ru' ? 'Пакет' : lang === 'ja' ? 'パッケージ' : lang === 'ko' ? '패키지' : 'Package', mice.packageName) : ''}
        ${mice.companyName ? SummaryRow(lang === 'th' ? 'บริษัท / หน่วยงาน' : lang === 'zh' ? '公司/机构' : lang === 'ru' ? 'Компания / Организация' : lang === 'ja' ? '企業・団体名' : lang === 'ko' ? '회사 / 단체명' : 'Company', mice.companyName) : ''}
        ${mice.specialRequests ? SummaryRow(t('specialRequests'), mice.specialRequests) : ''}
      </div>
    `;
  } else if (isSpa && state.spaDetails) {
    const spa = state.spaDetails;
    const spaOutletName = typeof spa.outletName === 'object'
      ? (spa.outletName[lang] || spa.outletName.en || spa.outletName.th || Object.values(spa.outletName)[0] || t('spaOutlet'))
      : (spa.outletName || t('spaOutlet'));
    const treatmentTitle = typeof spa.treatmentName === 'object'
      ? (spa.treatmentName[lang] || spa.treatmentName.en || spa.treatmentName.th || Object.values(spa.treatmentName)[0] || t('spaTreatment'))
      : (spa.treatmentName || selectedRoom.roomTypeName || t('spaTreatment'));
    const timeSlotDisplay = `${spa.timeSlot}${lang === 'th' ? ' น.' : ''}${spa.durationMinutes ? ` (${spa.durationMinutes} ${t('minutesUnit')})` : ''}`;

    detailsCardHtml = `
      <div class="summary-section">
        <h4>🌿 ${t('spaDetailsTitle')}</h4>
        ${SummaryRow(t('spaOutlet'), spaOutletName)}
        ${SummaryRow(t('spaTreatment'), treatmentTitle)}
        ${SummaryRow(t('serviceDate'), formatDate(spa.bookingDate))}
        ${SummaryRow(t('timeSlot'), timeSlotDisplay)}
        ${SummaryRow(t('guestsCount'), `${spa.pax} ${t('personUnit')}`)}
        ${spa.roomNumber ? SummaryRow(t('roomNumber'), `${t('roomPrefix')} ${spa.roomNumber}`) : ''}
      </div>
    `;
  } else if (isRest && state.restaurantDetails) {
    const rest = state.restaurantDetails;
    const restOutletName = typeof rest.outletName === 'object'
      ? (rest.outletName[lang] || rest.outletName.en || rest.outletName.th || Object.values(rest.outletName)[0] || t('diningOutlet'))
      : (rest.outletName || t('diningOutlet'));
    const timeSlotDisplay = `${rest.timeSlot}${lang === 'th' ? ' น.' : ''}`;
    const seatingAreaDisplay = typeof rest.seatingArea === 'object'
      ? (rest.seatingArea[lang] || rest.seatingArea.en || rest.seatingArea.th || Object.values(rest.seatingArea)[0])
      : (rest.seatingArea === 'Indoor (แอร์)'
          ? (lang === 'en' ? 'Indoor (A/C)' : lang === 'zh' ? '室内 (空调)' : lang === 'ru' ? 'В помещении (кондиционер)' : lang === 'ja' ? '屋内 (エアコン)' : lang === 'ko' ? '실내 (에어컨)' : 'Indoor (แอร์)')
          : rest.seatingArea === 'Outdoor (ริมสระ)'
            ? (lang === 'en' ? 'Outdoor (Poolside)' : lang === 'zh' ? '室外 (泳池边)' : lang === 'ru' ? 'На открытом воздухе (у бассейна)' : lang === 'ja' ? '屋外 (プールサイド)' : lang === 'ko' ? '야외 (수영장 옆)' : 'Outdoor (ริมสระ)')
            : rest.seatingArea === 'VIP Room'
              ? (lang === 'en' ? 'VIP Room' : lang === 'zh' ? 'VIP 贵宾房' : lang === 'ru' ? 'VIP комната' : lang === 'ja' ? 'VIPルーム' : lang === 'ko' ? 'VIP 룸' : 'VIP Room')
              : (rest.seatingArea || 'Standard'));

    detailsCardHtml = `
      <div class="summary-section">
        <h4>🍽️ ${t('diningDetailsTitle')}</h4>
        ${SummaryRow(t('diningOutlet'), restOutletName)}
        ${SummaryRow(t('seatingArea'), seatingAreaDisplay)}
        ${SummaryRow(t('serviceDate'), formatDate(rest.bookingDate))}
        ${SummaryRow(t('timeSlot'), timeSlotDisplay)}
        ${SummaryRow(t('partySize'), `${rest.partySize} ${t('personUnit')}`)}
        ${rest.dietaryRestrictions ? SummaryRow(t('specialRequests'), rest.dietaryRestrictions) : ''}
        ${rest.roomNumber ? SummaryRow(t('roomNumber'), `${t('roomPrefix')} ${rest.roomNumber}`) : ''}
      </div>
    `;
  } else {
    detailsCardHtml = `
      <div class="summary-section">
        <h4>📅 ${t('checkIn')} / ${t('checkOut')}</h4>
        ${SummaryRow(t('checkIn'), formatDate(search?.checkIn || ''))}
        ${SummaryRow(t('checkOut'), formatDate(search?.checkOut || ''))}
        ${SummaryRow(t('nights'), `${nights} ${t('nights')}`)}
      </div>
      
      <div class="summary-section">
        <h4>🛏️ ${t('roomType')}</h4>
        ${SummaryRow(t('roomType'), translateRoomType(selectedRoom.roomTypeCode, selectedRoom.roomTypeName))}
        ${SummaryRow(t('ratePlan'), translateRatePlan(selectedRoom.ratePlanCode, selectedRoom.ratePlanName))}
        ${SummaryRow(t('rooms'), (search?.rooms || 1).toString())}
        ${SummaryRow(`${t('adults')} / ${t('children')}`, `${search?.adults || 1} / ${search?.children || 0}`)}
      </div>
    `;
  }

  return `
    ${Header(t('confirmTitle'), false)}
    
    <div class="page-content" id="confirm-content">
      ${Card(`
        <h3 class="card-title">${t('bookingSummary')}</h3>
        
        ${detailsCardHtml}
        
        <div class="summary-section">
          <h4>👤 ${isAppointment ? t('registrantInfoTitle') : isRfp ? t('requesterInfoTitle') : isMice ? (lang === 'th' ? 'ข้อมูลผู้ติดต่อ / ผู้จัดงาน' : 'Organizer & Contact Info') : t('guestName')}</h4>
          ${SummaryRow(isAppointment ? t('registrantName') : isRfp ? t('requesterName') : t('guestName'), `${guestInfo.firstName} ${guestInfo.lastName}`)}
          ${SummaryRow(t('email'), guestInfo.email)}
          ${SummaryRow(t('phone'), guestInfo.phone)}
          ${guestInfo.specialRequests ? SummaryRow(t('specialRequests'), guestInfo.specialRequests) : ''}
        </div>
      `)}
      
      ${Card(
        `
        <div class="price-breakdown">
          ${
            isAppointment
              ? `${SummaryRow(
                  lang === 'th' ? 'ค่าธรรมเนียม / ค่าลงทะเบียน' : 'Registration Fee',
                  selectedRoom.totalRate > 0 ? formatCurrency(selectedRoom.totalRate, selectedRoom.currency) : (lang === 'th' ? 'ฟรี' : 'Free')
                )}
                ${selectedRoom.totalRate > 0 ? `
                  <div style="font-size: 11px; color: ${state.customFields?.vatType === 'non_vat' ? '#b45309' : '#15803d'}; text-align: right; margin-top: -6px; margin-bottom: 8px;">
                    ${state.customFields?.vatType === 'non_vat' ? (lang === 'th' ? '(ไม่มีภาษีมูลค่าเพิ่ม / Non-VAT)' : '(Non-VAT)') : (lang === 'th' ? '(รวมภาษีมูลค่าเพิ่ม 7% แล้ว)' : '(VAT 7% Included)')}
                    ${state.customFields?.serviceChargeType === 'service' ? (lang === 'th' ? ' • รวมค่าบริการแล้ว' : ' • Service Charge Included') : ''}
                  </div>
                ` : ''}`
              : isRfp
                ? SummaryRow(lang === 'th' ? 'สถานะใบเสนอราคา' : 'Quotation Status', lang === 'th' ? 'ไม่มีค่าใช้จ่ายล่วงหน้า' : 'No Upfront Charge')
                : isSpa
                  ? SummaryRow(t('spaRate'), formatCurrency(selectedRoom.totalRate, selectedRoom.currency))
                  : isRest
                    ? SummaryRow(t('diningDetailsTitle'), selectedRoom.totalRate > 0 ? formatCurrency(selectedRoom.totalRate, selectedRoom.currency) : t('freeReservation'))
                    : selectedRoom.numberOfRooms > 1
                      ? `${SummaryRow(
                          `${t('roomRate')} (${selectedRoom.numberOfRooms} ${t('rooms')})`,
                          `${selectedRoom.numberOfRooms} × ${formatCurrency(selectedRoom.ratePerRoom, selectedRoom.currency)} = ${formatCurrency(selectedRoom.totalRate, selectedRoom.currency)}`
                        )}`
                      : SummaryRow(
                          t('roomRate'),
                          formatCurrency(selectedRoom.totalRate, selectedRoom.currency)
                        )
          }
          ${
            state.extraCharges
              ? `
            ${SummaryRow(
              t('extraCharges') || 'ค่าบริการเพิ่ม',
              formatCurrency(state.extraCharges.totalExtraCharge, selectedRoom.currency),
              false
            )}
          `
              : ''
          }
        </div>
        <div class="total-section">
          <div class="total-label">${t('totalAmount')}</div>
          <div class="total-amount">${formatCurrency(totalRateWithExtras, selectedRoom.currency)}</div>
        </div>
      `,
        'total-card'
      )}
      
      ${selectedRoom.isRefundable === false ? `
      <div style="margin:0 0 12px; padding:12px 14px; background:#fee2e2; border:1px solid #fca5a5; border-radius:8px; font-size:13px; color:#991b1b; text-align:center;">
        <div style="font-weight:700; margin-bottom:4px;">${t('nonRefundable')}</div>
        <div>${t('nonRefundableWarning')}</div>
      </div>
      ` : ''}
      <div class="form-actions" style="margin-top: 16px;">
        <button 
          type="button" 
          class="btn btn-primary btn-block btn-lg"
          onclick="window.confirmBooking()"
        >
          ✓ ${t('confirmBooking')}
        </button>
      </div>
    </div>

    ${BackToTopButton('confirm')}

    ${FloatingNav(true)}
  `;
}

/**
 * Initialize confirm page event handlers
 */
export function initConfirmPage(): void {
  // Back button handler
  window.handleBack = () => {
    navigateTo('guest-info');
  };

  // Confirm booking handler
  window.confirmBooking = async () => {
    // Check if we're in edit mode
    const editBookingId = sessionStorage.getItem('editBookingId');
    const isEditMode = !!editBookingId;

    // Get fresh state when confirming
    const state = getState();
    const { search, selectedRoom, guestInfo, user, extraCharges } = state;

    console.log('Booking state:', {
      search,
      selectedRoom,
      guestInfo,
      user,
      extraCharges,
      isEditMode,
      editBookingId,
    });

    const isSpa = state.bookingType === 'SPA';
    const isRest = state.bookingType === 'RESTAURANT';
    const isMice = state.bookingType === 'MICE';
    const isAppointment = state.bookingType === 'APPOINTMENT' || state.engine === 'appointment';
    const isRfp = state.bookingType === 'RFP' || state.engine === 'request_rfp';
    const spa = state.spaDetails;
    const rest = state.restaurantDetails;
    const mice = state.miceDetails;
    const isServiceBooking = isSpa || isRest || isMice || isAppointment || isRfp;

    if (!selectedRoom || (!isServiceBooking && !search) || !guestInfo) {
      console.error('Missing required data:', {
        search: !!search,
        selectedRoom: !!selectedRoom,
        guestInfo: !!guestInfo,
      });
      setError(t('errorGeneric'));
      return;
    }

    setLoading(true);

    try {
      // Calculate total rate including extra charges
      const totalRateWithExtras = selectedRoom.totalRate + (extraCharges?.totalExtraCharge || 0);

      if (isEditMode && editBookingId) {
        // Update existing booking via PUT
        console.log('[Confirm] Updating booking:', editBookingId);

        const { apiFetch } = await import('../api');
        const { getTenantId } = await import('../config');
        const tenantId = getTenantId();

        const updateData: any = {
          bookingType: state.bookingType || (isSpa ? 'SPA' : isRest ? 'RESTAURANT' : isMice ? 'MICE' : 'ROOM'),
          checkIn: isSpa ? spa?.bookingDate : isRest ? rest?.bookingDate : isMice ? mice?.eventDate : (search?.checkIn || ''),
          checkOut: isSpa ? spa?.bookingDate : isRest ? rest?.bookingDate : isMice ? (mice?.endDate || mice?.eventDate) : (search?.checkOut || ''),
          adults: isSpa ? (spa?.pax || 1) : isRest ? (rest?.partySize || 1) : isMice ? (mice?.expectedPax || 20) : (search?.adults || 1),
          children: search?.children || 0,
          rooms: search?.rooms || 1,
          roomTypeCode: selectedRoom.roomTypeCode,
          roomTypeName: isSpa ? selectedRoom.roomTypeName : isRest ? selectedRoom.roomTypeName : isMice ? selectedRoom.roomTypeName : translateRoomType(selectedRoom.roomTypeCode, selectedRoom.roomTypeName),
          ratePlanCode: selectedRoom.ratePlanCode,
          ratePlanName: isSpa ? selectedRoom.ratePlanName : isRest ? selectedRoom.ratePlanName : isMice ? selectedRoom.ratePlanName : translateRatePlan(selectedRoom.ratePlanCode, selectedRoom.ratePlanName),
          totalRate: totalRateWithExtras,
          totalAmount: totalRateWithExtras,
          ratePerRoom: selectedRoom.ratePerRoom,
          totalBeforeTax: selectedRoom.totalBeforeTax,
          currency: selectedRoom.currency,
          guestFirstName: guestInfo.firstName,
          guestLastName: guestInfo.lastName,
          guestEmail: guestInfo.email,
          guestPhone: guestInfo.phone,
          specialRequests: isRest && rest?.dietaryRestrictions ? `${guestInfo.specialRequests ? guestInfo.specialRequests + ', ' : ''}อาหาร: ${rest.dietaryRestrictions}` : (guestInfo.specialRequests || null),
          language: getCurrentLanguage(),
          extraCharges: extraCharges || undefined,
          spaDetails: isSpa ? spa : undefined,
          restaurantDetails: isRest ? rest : undefined,
          miceDetails: isMice ? mice : undefined,
        };

        const result = await apiFetch<{ success: boolean; data: any; error?: string }>(
          `/api/v1/${tenantId}/bookings/${editBookingId}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updateData),
          }
        );

        if (result.success && result.data) {
          // Clear edit mode data
          sessionStorage.removeItem('editBookingId');
          sessionStorage.removeItem('editBookingData');
          resetSentConfirmation(editBookingId);

          const finalBooking = {
            ...updateData,
            ...result.data,
            confirmationNumber: result.data.confirmationNumber || result.data.id || editBookingId,
            id: result.data.id || editBookingId,
            guestName: `${guestInfo.firstName || ''} ${guestInfo.lastName || ''}`.trim() || 'Valued Guest',
            guestEmail: guestInfo.email,
            guestPhone: guestInfo.phone,
            totalAmount: totalRateWithExtras,
            bookingType: state.bookingType,
            spaDetails: result.data.spaDetails || updateData.spaDetails || state.spaDetails,
            restaurantDetails: result.data.restaurantDetails || updateData.restaurantDetails || state.restaurantDetails,
            miceDetails: result.data.miceDetails || updateData.miceDetails || state.miceDetails,
          };

          setBooking(finalBooking);
          navigateTo('success');
        } else {
          const errorMsg = result.error || t('errorUpdatingBooking');
          alert(errorMsg);
          setError(errorMsg);
        }
      } else {
        // Create new booking via POST
        console.log('[Confirm] Creating new booking');

        const todayStr = new Date().toISOString().split('T')[0];
        const bookingType = state.bookingType || 'ROOM';
        const isAppointment = bookingType === 'APPOINTMENT' || state.engine === 'appointment';
        const isSpa = bookingType === 'SPA';
        const isRest = bookingType === 'RESTAURANT';
        const isMice = bookingType === 'MICE';
        const isRfp = bookingType === 'RFP' || state.engine === 'request-rfp' || state.engine === 'request_rfp';

        const checkInVal = (isSpa || isAppointment) ? state.spaDetails?.bookingDate : (isRest ? state.restaurantDetails?.bookingDate : (isMice ? state.miceDetails?.eventDate : (search?.checkIn || todayStr)));
        const checkOutVal = (isSpa || isAppointment) ? state.spaDetails?.bookingDate : (isRest ? state.restaurantDetails?.bookingDate : (isMice ? (state.miceDetails?.endDate || state.miceDetails?.eventDate) : (search?.checkOut || todayStr)));
        
        const checkInDate = new Date(checkInVal || todayStr);
        const checkOutDate = new Date(checkOutVal || todayStr);
        const calculatedNights = Math.max(1, Math.round((checkOutDate.getTime() - checkInDate.getTime()) / 86400000));
        const nights = search?.nights || calculatedNights;
        const guestFullName = `${guestInfo.firstName || ''} ${guestInfo.lastName || ''}`.trim() || 'Valued Guest';
        const channel = (typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('_channel') || localStorage.getItem('_channel')) : null) || 'line';
        const openid = typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('_openid') || localStorage.getItem('_openid')) : null;
        const phone = typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('_phone') || localStorage.getItem('_phone')) : null;

        const bookingRequest: BookingRequest = {
          tenantId: config.tenantId,
          channel,
          channelUserId: openid || phone || user?.userId || 'demo-user-001',
          openid: openid || undefined,
          lineUserId: user?.userId || 'demo-user-001',
          lineUserDisplayName: user?.displayName,
          lineUserPictureUrl: user?.pictureUrl,
          bookingType,
          engine: state.engine,
          serviceId: state.activeServiceId,
          resourceId: state.resourceId,
          resourceName: state.resourceName,
          customFields: state.customFields || {},
          spaDetails: state.spaDetails,
          restaurantDetails: state.restaurantDetails,
          miceDetails: state.miceDetails,
          checkIn: checkInVal,
          checkOut: checkOutVal,
          nights,
          adults: (bookingType === 'SPA' || bookingType === 'APPOINTMENT') ? (state.spaDetails?.pax || 1) : (bookingType === 'RESTAURANT' ? (state.restaurantDetails?.partySize || 1) : (bookingType === 'MICE' || bookingType === 'RFP' ? (state.miceDetails?.expectedPax || 20) : (search?.adults || 1))),
          children: search?.children || 0,
          rooms: search?.rooms || 1,
          roomTypeCode: selectedRoom.roomTypeCode,
          roomTypeName: isMice || isAppointment || isRfp ? selectedRoom.roomTypeName : translateRoomType(selectedRoom.roomTypeCode, selectedRoom.roomTypeName),
          ratePlanCode: selectedRoom.ratePlanCode,
          ratePlanName: isMice || isAppointment || isRfp ? selectedRoom.ratePlanName : translateRatePlan(selectedRoom.ratePlanCode, selectedRoom.ratePlanName),
          totalRate: totalRateWithExtras,
          ratePerRoom: selectedRoom.ratePerRoom,
          totalBeforeTax: selectedRoom.totalBeforeTax,
          currency: selectedRoom.currency,
          guest: guestInfo,
          guestName: guestFullName,
          guestEmail: guestInfo.email,
          guestPhone: guestInfo.phone,
          language: getCurrentLanguage(),
          extraCharges: extraCharges || undefined,
          isRefundable: selectedRoom.isRefundable,
          specialRequests: state.miceDetails?.specialRequests || guestInfo.specialRequests || undefined,
          requirements: state.miceDetails?.specialRequests || undefined,
        };

        console.log('Sending booking request:', bookingRequest);

        const confirmation = await createBooking(bookingRequest);
        const finalBooking = {
          ...bookingRequest,
          ...confirmation,
          guestName: guestFullName,
          guestEmail: guestInfo.email,
          guestPhone: guestInfo.phone,
          nights,
          totalAmount: totalRateWithExtras,
          bookingType: state.bookingType,
          spaDetails: confirmation?.spaDetails || state.spaDetails,
          restaurantDetails: confirmation?.restaurantDetails || state.restaurantDetails,
          miceDetails: confirmation?.miceDetails || state.miceDetails,
        };
        setBooking(finalBooking);

        // Save guest info locally for immediate auto-fill next time
        try {
          saveGuestInfoToStorage(guestInfo);
          const cnf = confirmation?.confirmationNumber || finalBooking.confirmationNumber;
          if (cnf) {
            const keys = ['my_booking_codes', `my_booking_codes_${channel}`];
            for (const key of keys) {
              const existingCodes: string[] = JSON.parse(localStorage.getItem(key) || '[]');
              if (!existingCodes.includes(cnf)) {
                existingCodes.push(cnf);
                localStorage.setItem(key, JSON.stringify(existingCodes.slice(-20)));
              }
            }
          }
        } catch (_) {}

        // Save guest info to Firestore for auto-fill next time
        if (user?.userId) {
          console.log('[Confirm] Saving user profile to Firestore...');
          saveUserProfile(user.userId, {
            firstName: guestInfo.firstName,
            lastName: guestInfo.lastName,
            email: guestInfo.email,
            phone: guestInfo.phone,
            title: guestInfo.title,
            nationality: guestInfo.nationality,
            idType: guestInfo.idType,
            idNumber: guestInfo.idNumber,
          })
            .then(success => {
              if (success) {
                console.log('[Confirm] User profile saved successfully');
              } else {
                console.warn('[Confirm] Failed to save user profile');
              }
            })
            .catch(err => {
              console.error('[Confirm] Error saving user profile:', err);
            });
        }

        // For Pay at Venue (Spa, Restaurant, MICE, Appointment, RFP, or pay_at_hotel), navigate directly to success page
        if (
          isSpa ||
          isRest ||
          isMice ||
          isAppointment ||
          isRfp ||
          String(bookingType) === 'SPA' ||
          String(bookingType) === 'RESTAURANT' ||
          String(bookingType) === 'MICE' ||
          String(bookingType) === 'APPOINTMENT' ||
          String(bookingType) === 'RFP' ||
          String(confirmation?.paymentStatus) === 'pay_at_hotel' ||
          String(confirmation?.paymentStatus) === 'pay_at_venue' ||
          totalRateWithExtras === 0
        ) {
          navigateTo('success');
        } else {
          navigateTo('payment');
        }
      }
    } catch (error) {
      console.error('Booking operation failed:', error);
      setError(t('errorBookingFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Expose forward action to footer (calls confirmBooking)
  window.handleForward = () => {
    if (window.confirmBooking) {
      window.confirmBooking();
    }
  };

  // Floating "BACK TO TOP" for Confirm page
  initBackToTop('confirm');
}

// Extend window for global handlers
declare global {
  interface Window {
    confirmBooking: () => void;
    handleForward?: () => void;
  }
}
